import { cn, Pill, PillProps } from 'component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ConservationBenchmarkType, TrendLabel } from '@/api/rest/resources/conservation';
import { Logger } from '@/lib/logs/logger';
import { getStyleForConservationBenchmark } from '@/utils/getStyleForBenchmark';
import { resolveThemeColor } from '@/utils/resolveThemeColor';

type BenchmarkPillProps = PillProps & {
  benchmark: ConservationBenchmarkType;
};

export const BenchmarkPill = ({ benchmark, className, ...delegated }: BenchmarkPillProps) => {
  const { t } = useTranslation();

  return (
    <PillBase style={getStyleForConservationBenchmark(benchmark)} className={className} {...delegated}>
      {t(`shared.projects.project.conservation.benchmark.${benchmark}`)}
    </PillBase>
  );
};

type TrendPillProps = PillProps & {
  trendLabel: TrendLabel;
  variant?: 'default' | 'negative';
};

export const TrendPill = ({ trendLabel, className, variant = 'default', ...delegated }: TrendPillProps) => {
  const { t } = useTranslation();

  const style = getStyleForTrendLabel(trendLabel, variant);

  return (
    <PillBase style={style} className={className} {...delegated}>
      {t(`shared.projects.project.conservation.trendLabel.${trendLabel}`)}
    </PillBase>
  );
};

const PillBase = ({ children, className, ...delegated }: { children: ReactNode } & PillProps) => {
  return (
    <Pill className={cn('font-bold uppercase rounded-lg py-1 px-2 tracking-[1.2px]', className)} {...delegated}>
      {children}
    </Pill>
  );
};

const exhaustivenessTrendLabelCheck = (trendLabel: never) => {
  Logger.error(`No color defined for ´trend label type "${trendLabel}"`);
  return { background: resolveThemeColor('transparent'), text: resolveThemeColor('transparent') };
};

const getStyleForTrendLabel = (trendLabel: TrendLabel | never, variant: 'default' | 'negative') => {
  const positiveLabelStyles = {
    background: '#BFFFE6',
    color: '#285E2B',
  };
  const negativeLabelStyles = {
    background: '#F6D4C1',
    color: '#774200',
  };

  switch (trendLabel) {
    case TrendLabel.increasing:
      return variant === 'default' ? positiveLabelStyles : negativeLabelStyles;
    case TrendLabel.stable:
      return {
        background: '#CFFFBF',
        color: '#285E2B',
      };
    case TrendLabel.decreasing:
      return variant === 'default' ? negativeLabelStyles : positiveLabelStyles;
    default:
      return exhaustivenessTrendLabelCheck(trendLabel);
  }
};
